<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Type Select -->
              <base-select-input
                col="6"
                :optionsList="typeOptions"
                :placeholder="$t('PLACEHOLDERS.type_filter')"
                v-model="filterOptions.type"
              />
              <!-- End:: Type Select -->

              <!-- Start:: Period Filter -->
              <base-select-input
                col="6"
                :optionsList="periodOptions"
                :placeholder="$t('PLACEHOLDERS.period_filter')"
                v-model="filterOptions.period"
              />
              <!-- End:: Period Filter -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Page Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>
            {{ $t("PLACEHOLDERS.packages_bundles_reports") }}
          </h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <base-button
            class="mt-0 excel_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadExcel')"
            @fireClick="downloadExcel"
            :disabled="excelDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-excel"></i>
            </template>
          </base-button>
          <base-button
            class="mt-0 pdf_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadPdf')"
            @fireClick="downloadPdf"
            :disabled="pdfDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-pdf"></i>
            </template>
          </base-button>
        </div>
      </div>
      <!--  =========== End:: Page Title =========== -->

      <!--  =========== Start:: Statistics Cards =========== -->
      <div class="statistics_wrapper" v-if="!loading">
        <div class="row">
          <!-- Total Added -->
          <div class="col-lg-3 col-md-6 mb-4">
            <div class="statistics_card">
              <div class="card_icon total_added">
                <i class="fal fa-plus-circle"></i>
              </div>
              <div class="card_content">
                <h3>{{ statistics.total_added || 0 }}</h3>
                <p>{{ $t("PLACEHOLDERS.total_added") }}</p>
              </div>
            </div>
          </div>

          <!-- Total Reserved -->
          <div class="col-lg-3 col-md-6 mb-4">
            <div class="statistics_card">
              <div class="card_icon total_reserved">
                <i class="fal fa-bookmark"></i>
              </div>
              <div class="card_content">
                <h3>{{ statistics.total_reserved || 0 }}</h3>
                <p>{{ $t("PLACEHOLDERS.total_reserved") }}</p>
              </div>
            </div>
          </div>

          <!-- Total Pending -->
          <div class="col-lg-3 col-md-6 mb-4">
            <div class="statistics_card">
              <div class="card_icon total_pending">
                <i class="fal fa-clock"></i>
              </div>
              <div class="card_content">
                <h3>{{ statistics.total_pending || 0 }}</h3>
                <p>{{ $t("PLACEHOLDERS.total_pending") }}</p>
              </div>
            </div>
          </div>

          <!-- Total Received -->
          <div class="col-lg-3 col-md-6 mb-4">
            <div class="statistics_card">
              <div class="card_icon total_received">
                <i class="fal fa-check-circle"></i>
              </div>
              <div class="card_content">
                <h3>{{ statistics.total_received || 0 }}</h3>
                <p>{{ $t("PLACEHOLDERS.total_received") }}</p>
              </div>
            </div>
          </div>

          <!-- Total In Transit -->
          <div class="col-lg-3 col-md-6 mb-4">
            <div class="statistics_card">
              <div class="card_icon total_in_transit">
                <i class="fal fa-shipping-fast"></i>
              </div>
              <div class="card_content">
                <h3>{{ statistics.total_in_transit || 0 }}</h3>
                <p>{{ $t("PLACEHOLDERS.total_in_transit") }}</p>
              </div>
            </div>
          </div>

          <!-- Total Delivered -->
          <div class="col-lg-3 col-md-6 mb-4">
            <div class="statistics_card">
              <div class="card_icon total_delivered">
                <i class="fal fa-box-check"></i>
              </div>
              <div class="card_content">
                <h3>{{ statistics.total_delivered || 0 }}</h3>
                <p>{{ $t("PLACEHOLDERS.total_delivered") }}</p>
              </div>
            </div>
          </div>

          <!-- Total Returned -->
          <div class="col-lg-3 col-md-6 mb-4">
            <div class="statistics_card">
              <div class="card_icon total_returned">
                <i class="fal fa-undo"></i>
              </div>
              <div class="card_content">
                <h3>{{ statistics.total_returned || 0 }}</h3>
                <p>{{ $t("PLACEHOLDERS.total_returned") }}</p>
              </div>
            </div>
          </div>

          <!-- Total Delayed -->
          <div class="col-lg-3 col-md-6 mb-4">
            <div class="statistics_card">
              <div class="card_icon total_delayed">
                <i class="fal fa-exclamation-triangle"></i>
              </div>
              <div class="card_content">
                <h3>{{ statistics.total_delayed || 0 }}</h3>
                <p>{{ $t("PLACEHOLDERS.total_delayed") }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div class="loading_wrapper" v-else>
        <v-progress-circular
          indeterminate
          color="primary"
          :size="70"
        ></v-progress-circular>
      </div>
      <!--  =========== End:: Statistics Cards =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Generate PDF Template Content -->
    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="true"
      :preview-modal="true"
      :filename="$t('PLACEHOLDERS.packages_bundles_reports')"
      :pdf-quality="2"
      pdf-format="a4"
      :manual-pagination="false"
      :paginate-elements-by-height="1400"
      pdf-content-width="100%"
      @progress="pdfDownloadBtnIsLoading = true"
      @hasGenerated="handlePdfGenerated"
      ref="html2Pdf"
    >
      <section slot="pdf-content">
        <div class="pdf_file_content">
          <h3 class="file_title">
            {{ $t("PLACEHOLDERS.packages_bundles_reports") }}
          </h3>
          <div class="pdf_statistics">
            <table class="statistics_table">
              <thead>
                <tr>
                  <th>{{ $t("PLACEHOLDERS.statistic_name") }}</th>
                  <th>{{ $t("PLACEHOLDERS.value") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ $t("PLACEHOLDERS.total_added") }}</td>
                  <td>{{ statistics.total_added || 0 }}</td>
                </tr>
                <tr>
                  <td>{{ $t("PLACEHOLDERS.total_reserved") }}</td>
                  <td>{{ statistics.total_reserved || 0 }}</td>
                </tr>
                <tr>
                  <td>{{ $t("PLACEHOLDERS.total_pending") }}</td>
                  <td>{{ statistics.total_pending || 0 }}</td>
                </tr>
                <tr>
                  <td>{{ $t("PLACEHOLDERS.total_received") }}</td>
                  <td>{{ statistics.total_received || 0 }}</td>
                </tr>
                <tr>
                  <td>{{ $t("PLACEHOLDERS.total_in_transit") }}</td>
                  <td>{{ statistics.total_in_transit || 0 }}</td>
                </tr>
                <tr>
                  <td>{{ $t("PLACEHOLDERS.total_delivered") }}</td>
                  <td>{{ statistics.total_delivered || 0 }}</td>
                </tr>
                <tr>
                  <td>{{ $t("PLACEHOLDERS.total_returned") }}</td>
                  <td>{{ statistics.total_returned || 0 }}</td>
                </tr>
                <tr>
                  <td>{{ $t("PLACEHOLDERS.total_delayed") }}</td>
                  <td>{{ statistics.total_delayed || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </vue-html2pdf>
    <!-- End:: Generate PDF Template Content -->
  </div>
</template>

<script>
import VueHtml2pdf from "vue-html2pdf";
import { mapGetters } from "vuex";

export default {
  name: "AllPackagesBundlesReports",

  components: {
    VueHtml2pdf,
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    typeOptions() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.bundles"),
          value: "bundles",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.packages"),
          value: "packages",
        },
      ];
    },

    periodOptions() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.daily_period"),
          value: "daily",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.weekly_period"),
          value: "weekly",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.monthly_period"),
          value: "monthly",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      pdfDownloadBtnIsLoading: false,
      excelDownloadBtnIsLoading: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        type: {
          id: 1,
          name: this.$t("PLACEHOLDERS.bundles"),
          value: "bundles",
        },
        period: null,
      },
      // End:: Filter Data

      // Start:: Statistics Data
      statistics: {
        total_added: 0,
        total_reserved: 0,
        total_pending: 0,
        total_received: 0,
        total_in_transit: 0,
        total_delivered: 0,
        total_returned: 0,
        total_delayed: 0,
      },
      // End:: Statistics Data
    };
  },

  methods: {
    async submitFilterForm() {
      this.getStatistics();
    },

    async resetFilter() {
      this.filterOptions.type.value = {
        id: 1,
        name: this.$t("PLACEHOLDERS.bundles"),
        value: "bundles",
      };
      this.filterOptions.period = null;
      this.getStatistics();
    },

    async getStatistics() {
      this.loading = true;

      try {
        let res = await this.$axios({
          method: "GET",
          url: "reports/packages-bundles",
          params: {
            type: this.filterOptions.type?.value,
            period: this.filterOptions.period?.value,
          },
        });

        this.loading = false;
        this.statistics = res.data.data || {};
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },

    async downloadExcel() {
      this.excelDownloadBtnIsLoading = true;

      try {
        const res = await this.$axios({
          method: "GET",
          url: "reports/packages-bundles",
          params: {
            format: "excel",
            type: this.filterOptions.type?.value,
            period: this.filterOptions.period?.value,
          },
          responseType: "blob",
        });

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${this.$t(
            "PLACEHOLDERS.packages_bundles_reports"
          )}_${new Date().getTime()}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.excelDownloadBtnIsLoading = false;
        this.$message.success(this.$t("MESSAGES.downloadedSuccessfully"));
      } catch (error) {
        this.excelDownloadBtnIsLoading = false;
        console.log(error.response?.data?.message);
        this.$message.error(this.$t("MESSAGES.downloadFailed"));
      }
    },

    async downloadPdf() {
      await this.$refs.html2Pdf.generatePdf();
    },

    handlePdfGenerated() {
      this.pdfDownloadBtnIsLoading = false;
      this.$message.success(this.$t("MESSAGES.generatedSuccessfully"));
    },
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });

    this.getStatistics();
  },
};
</script>

<style lang="scss" scoped>
.statistics_wrapper {
  margin-top: 30px;
  padding: 20px;
}

.statistics_card {
  background: var(--theme_second_bg_clr);
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 2px 8px var(--simple_shadow_clr);
  display: flex;
  align-items: center;
  gap: 20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 16px var(--mid_shadow_clr);
  }

  .card_icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: white;

    &.total_added {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    &.total_reserved {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    }

    &.total_pending {
      background: linear-gradient(135deg, #ffa726 0%, #fb8c00 100%);
    }

    &.total_received {
      background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
    }

    &.total_in_transit {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    }

    &.total_delivered {
      background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
    }

    &.total_returned {
      background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
    }

    &.total_delayed {
      background: linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%);
    }
  }

  .card_content {
    flex: 1;

    h3 {
      font-size: 32px;
      font-weight: bold;
      margin: 0 0 5px 0;
      color: var(--theme_text_clr);
    }

    p {
      font-size: 14px;
      color: var(--theme_secondary_text_clr);
      margin: 0;
    }
  }
}

.loading_wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.pdf_file_content {
  padding: 20px;
  background-color: white;

  .file_title {
    text-align: center;
    margin-bottom: 30px;
    color: #333;
    font-size: 24px;
  }

  .pdf_statistics {
    margin-top: 20px;

    .statistics_table {
      width: 100%;
      border-collapse: collapse;

      th,
      td {
        padding: 12px;
        text-align: left;
        border: 1px solid #ddd;
      }

      th {
        background-color: #f5f5f5;
        font-weight: bold;
        color: #333;
      }

      tr:nth-child(even) {
        background-color: #f9f9f9;
      }
    }
  }
}

// Dark Theme Support
:root .dark_theme {
  .statistics_wrapper {
    background-color: transparent;
  }

  .statistics_card {
    background: var(--theme_second_bg_clr);
    box-shadow: 0 2px 8px var(--simple_shadow_clr);

    &:hover {
      box-shadow: 0 4px 16px var(--mid_shadow_clr);
    }

    .card_content {
      h3 {
        color: var(--theme_text_clr);
      }

      p {
        color: var(--theme_secondary_text_clr);
        opacity: 0.9;
      }
    }
  }

  .pdf_file_content {
    background-color: var(--theme_second_bg_clr);

    .file_title {
      color: var(--theme_text_clr);
    }
  }
}
</style>
